import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import {
  batchId,
  clock,
  FG_STAGING_ZONE_CODE,
  fgSkuId,
  HISTORIC,
  labelHead,
  OPENING_PALLETS,
  packPer,
  palletId,
  zoneId
} from './wms-fg.seed'
import { type OfgActionName, type OfgStatusCode, WF_OFG } from './workflow-ofg.seed'

/**
 * Outbound FG (C09) — ERD v010: delivery_plan, delivery_plan_line, fg_picking_task(+_item), fg_staging, fg_label.
 * Angka mengikuti prototype v7.44: 7 order historis (Forecast kedaluwarsa, Forecast stok cukup, 5 Shipped) ditambah tiga
 * order berjalan (Released / Staging / Staged) dan satu Forecast stok kurang untuk demo.
 *
 * Stok awal Karton per pallet (`OPENING_PALLETS`) dimaterialisasi menjadi baris `fg_label` per Karton (batch historis
 * paling tua menempati pallet paling awal), supaya alokasi FIFO bisa memilih label sungguhan.
 *
 * Kolom di luar ERD (diusulkan, laporan 13): `delivery_plan_line.unit_type`; `fg_picking_task.seq`;
 * `fg_picking_task_item.item_status`, `container_id`; `fg_label.shipped_plan_id`; tabel `fg_rts_task` / `fg_rts_task_item`
 * (Return-to-Stock, tidak ada di ERD).
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()
const audit = (userId: string | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

const ulil = USER_IDS.ulil
const budi = USER_IDS.budi
const sari = USER_IDS.sari

export const OUTBOUND_STAGING_ZONE_ID = zoneId(FG_STAGING_ZONE_CODE)

const CUSTOMER_CODES = ['CUST-SRD', 'CUST-BN', 'CUST-MKS', 'DEPO-KDS', 'CUST-EVAN'] as const
type CustomerCode = (typeof CUSTOMER_CODES)[number]
export const customerId = (code: CustomerCode) => seedId(SEED_GROUP.company, 100 + CUSTOMER_CODES.indexOf(code) + 1)

const GTIN: Record<string, string> = {
  'TBC-ONE-12': '48997034030618',
  'TBC-ONE-F16': '48997034030625',
  'TBC-BOLD-20': '48997034030632',
  'TBC-BOLD-20-SKT': '48997034030649',
  'TBC-XTRA-20': '48997034030656',
  'TBC-BOLD-12': '48997034030663',
  MILLIONS: '48997034030670',
  'BRIGHTON-RED': '48997034030687',
  'ARUM-MANIS': '48997034030694',
  'AMANA-K12': '48997034030700'
}
export const gtinOf = (sku: string) => GTIN[sku] ?? null

type Unit = 'Karton' | 'Bal' | 'Slop' | 'Pack'

/** Karton stok awal per SKU: pallet slot urut × batch historis urut (paling tua dulu) — FIFO sudah tercermin di urutan. */
type StockLabel = { id: string; sku: string; pallet: string; batchNo: string; no: number }
function buildStockLabels(): { rows: LocalRow[]; bySku: Map<string, StockLabel[]> } {
  const rows: LocalRow[] = []
  const bySku = new Map<string, StockLabel[]>()
  let counter = 0
  const karton = OPENING_PALLETS.filter(p => p[3] !== 'Bal')
  const skus = [...new Set(karton.map(p => p[1]))]
  skus.forEach(sku => {
    const slots = karton.filter(p => p[1] === sku).flatMap(([pallet, , n]) => Array.from({ length: n }, () => pallet))
    const batches = HISTORIC.filter(h => h[1] === sku && h[5] > 0)
      .sort((a, b) => a[2].localeCompare(b[2]))
      .flatMap(h => Array.from({ length: h[5] }, () => h[0]))
    const list: StockLabel[] = []
    slots.forEach((pallet, index) => {
      const batchNo = batches[index] ?? batches[batches.length - 1]
      if (!batchNo) return
      counter += 1
      const head = labelHead(batchNo)
      const no = list.filter(item => item.batchNo === batchNo).length + 1
      const row: LocalRow = {
        ...audit(ulil, '2026-08-01 07:00'),
        id: seedId(SEED_GROUP.fgLabel, 2000 + counter),
        label_no: `JLC:C:F:${head.short}:${head.seq}:${head.date}:${clock(6, 0, no * 7)}:B001:${String(1000 + no).padStart(4, '0')}`,
        sku_id: fgSkuId(sku),
        batch_id: batchId(batchNo),
        level: 'Karton',
        gtin: gtinOf(sku),
        parent_label_id: null,
        zone_id: zoneId(pallet.split('-')[0]),
        pallet_id: palletId(pallet),
        container_id: null,
        label_status: 'IN_STOCK',
        is_priority_lot: false
      }
      rows.push(row)
      list.push({ id: String(row.id), sku, pallet, batchNo, no })
    })
    bySku.set(sku, list)
  })
  return { rows, bySku }
}

/* ------------------------------------------------------------ order */

type PlanLine = { sku: string; unit: Unit; qty: number }
type TaskSpec = { operator: 'budi' | 'sari'; sku: string; unit: Unit; qty: number; staged: number }
type PlanSpec = {
  no: string
  customer: CustomerCode
  period: string
  status: OfgStatusCode
  createdAt: string
  lines: PlanLine[]
  sjNo?: string
  shippedAt?: string
  tasks?: TaskSpec[]
}

const PLANS: PlanSpec[] = [
  {
    no: 'PLAN-260710-090',
    customer: 'CUST-BN',
    period: '2026-07-W2',
    status: 'OFG_FORECAST',
    createdAt: '2026-07-10 09:00',
    lines: [{ sku: 'TBC-ONE-12', unit: 'Karton', qty: 5 }]
  },
  {
    no: 'PLAN-260818-070',
    customer: 'CUST-MKS',
    period: '2026-08-W4',
    status: 'OFG_FORECAST',
    createdAt: '2026-08-18 08:15',
    lines: [
      { sku: 'TBC-ONE-12', unit: 'Karton', qty: 1 },
      { sku: 'TBC-BOLD-20', unit: 'Karton', qty: 1 }
    ]
  },
  {
    no: 'PLAN-260818-072',
    customer: 'CUST-BN',
    period: '2026-08-W4',
    status: 'OFG_FORECAST',
    createdAt: '2026-08-18 08:20',
    lines: [
      { sku: 'ARUM-MANIS', unit: 'Karton', qty: 80 },
      { sku: 'TBC-XTRA-20', unit: 'Karton', qty: 2 }
    ]
  },
  {
    no: 'PLAN-260818-075',
    customer: 'CUST-SRD',
    period: '2026-08-W4',
    status: 'OFG_RELEASED',
    createdAt: '2026-08-18 08:30',
    lines: [
      { sku: 'TBC-ONE-12', unit: 'Karton', qty: 2 },
      { sku: 'TBC-BOLD-12', unit: 'Karton', qty: 1 }
    ],
    tasks: [
      { operator: 'budi', sku: 'TBC-ONE-12', unit: 'Karton', qty: 2, staged: 0 },
      { operator: 'sari', sku: 'TBC-BOLD-12', unit: 'Karton', qty: 1, staged: 0 }
    ]
  },
  {
    no: 'PLAN-260818-076',
    customer: 'CUST-MKS',
    period: '2026-08-W4',
    status: 'OFG_STAGING',
    createdAt: '2026-08-18 08:40',
    lines: [
      { sku: 'TBC-XTRA-20', unit: 'Karton', qty: 2 },
      { sku: 'MILLIONS', unit: 'Karton', qty: 1 }
    ],
    tasks: [
      { operator: 'budi', sku: 'TBC-XTRA-20', unit: 'Karton', qty: 2, staged: 1 },
      { operator: 'sari', sku: 'MILLIONS', unit: 'Karton', qty: 1, staged: 1 }
    ]
  },
  {
    no: 'PLAN-260818-077',
    customer: 'CUST-BN',
    period: '2026-08-W4',
    status: 'OFG_STAGED',
    createdAt: '2026-08-18 08:50',
    lines: [{ sku: 'TBC-BOLD-20', unit: 'Karton', qty: 2 }],
    tasks: [{ operator: 'budi', sku: 'TBC-BOLD-20', unit: 'Karton', qty: 2, staged: 2 }]
  },
  {
    no: 'PLAN-260812-080',
    customer: 'CUST-MKS',
    period: '2026-08-W2',
    status: 'OFG_SHIPPED',
    createdAt: '2026-08-12 08:30',
    shippedAt: '2026-08-14 15:00',
    lines: [{ sku: 'TBC-BOLD-20', unit: 'Karton', qty: 4 }]
  },
  {
    no: 'PLAN-260805-010',
    customer: 'CUST-MKS',
    period: '2026-08',
    status: 'OFG_SHIPPED',
    createdAt: '2026-08-07 14:20',
    shippedAt: '2026-08-07 14:20',
    sjNo: '001/SJ/08/2026',
    lines: [
      { sku: 'TBC-BOLD-20', unit: 'Karton', qty: 3 },
      { sku: 'TBC-ONE-12', unit: 'Karton', qty: 2 }
    ]
  },
  {
    no: 'PLAN-260812-020',
    customer: 'CUST-BN',
    period: '2026-08',
    status: 'OFG_SHIPPED',
    createdAt: '2026-08-18 10:05',
    shippedAt: '2026-08-18 10:05',
    sjNo: '002/SJ/08/2026',
    lines: [{ sku: 'TBC-ONE-12', unit: 'Karton', qty: 4 }]
  },
  {
    no: 'PLAN-260901-030',
    customer: 'CUST-MKS',
    period: '2026-09',
    status: 'OFG_SHIPPED',
    createdAt: '2026-09-03 09:40',
    shippedAt: '2026-09-03 09:40',
    sjNo: '003/SJ/09/2026',
    lines: [
      { sku: 'TBC-BOLD-12', unit: 'Karton', qty: 3 },
      { sku: 'MILLIONS', unit: 'Karton', qty: 2 }
    ]
  },
  {
    no: 'PLAN-260814-040',
    customer: 'CUST-BN',
    period: '2026-08',
    status: 'OFG_SHIPPED',
    createdAt: '2026-08-16 11:30',
    shippedAt: '2026-08-16 11:30',
    sjNo: '004/SJ/08/2026',
    lines: [
      { sku: 'TBC-ONE-12', unit: 'Bal', qty: 2 },
      { sku: 'TBC-ONE-12', unit: 'Slop', qty: 3 }
    ]
  }
]

const STATUS_PATH: Record<OfgStatusCode, OfgActionName[]> = {
  OFG_FORECAST: [],
  OFG_PENDING_SPV: ['ACT_OFG_SUBMIT_RELEASE'],
  OFG_RELEASED: ['ACT_OFG_RELEASE_ORDER'],
  OFG_STAGING: ['ACT_OFG_RELEASE_ORDER', 'ACT_OFG_START_PICK'],
  OFG_STAGED: ['ACT_OFG_RELEASE_ORDER', 'ACT_OFG_START_PICK', 'ACT_OFG_COMPLETE_STAGING'],
  OFG_SHIPPED: ['ACT_OFG_RELEASE_ORDER', 'ACT_OFG_START_PICK', 'ACT_OFG_COMPLETE_STAGING', 'ACT_OFG_DISPATCH'],
  OFG_ARCHIVED: []
}
const ACTION_ACTOR: Record<string, 'ACT_OFG_ADMIN_FG' | 'ACT_OFG_OP_FG'> = {
  ACT_OFG_RELEASE_ORDER: 'ACT_OFG_ADMIN_FG',
  ACT_OFG_START_PICK: 'ACT_OFG_OP_FG',
  ACT_OFG_COMPLETE_STAGING: 'ACT_OFG_OP_FG',
  ACT_OFG_DISPATCH: 'ACT_OFG_ADMIN_FG'
}
const ACTION_USER: Record<string, string> = {
  ACT_OFG_RELEASE_ORDER: ulil,
  ACT_OFG_START_PICK: budi,
  ACT_OFG_COMPLETE_STAGING: budi,
  ACT_OFG_DISPATCH: ulil
}

/** Pallet yang berkurang karena Karton sudah diambil ke staging / dikirim pada seed — dipakai `applyOutboundStock`. */
export const OUTBOUND_PALLET_DRAWDOWN: Record<string, number> = {}

export function buildWmsOutboundSeed(): SeedTables {
  for (const key of Object.keys(OUTBOUND_PALLET_DRAWDOWN)) delete OUTBOUND_PALLET_DRAWDOWN[key]
  const { rows: stockLabels, bySku } = buildStockLabels()
  const fg_label: LocalRow[] = [...stockLabels]
  const delivery_plan: LocalRow[] = []
  const delivery_plan_line: LocalRow[] = []
  const fg_picking_task: LocalRow[] = []
  const fg_picking_task_item: LocalRow[] = []
  const fg_staging: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  const cursor = new Map<string, number>()
  let logNo = 0
  let itemNo = 0
  let stagingNo = 0
  let historicLabelNo = 0

  /** Ambil Karton FIFO berikutnya untuk SKU (indeks urut — label yang sudah dipakai dilewati). */
  const nextStock = (sku: string) => {
    const list = bySku.get(sku) ?? []
    const index = cursor.get(sku) ?? 0
    cursor.set(sku, index + 1)
    return list[index]
  }
  const labelRow = (id: string) => fg_label.find(row => row.id === id) as LocalRow

  PLANS.forEach((plan, index) => {
    const planId = seedId(SEED_GROUP.deliveryPlan, index + 1)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 1000 + index + 1)
    delivery_plan.push({
      ...audit(ulil, plan.createdAt),
      id: planId,
      plan_no: plan.no,
      customer_id: customerId(plan.customer),
      period: plan.period,
      status_id: WF_OFG.statusId(plan.status),
      submission_id: submissionId,
      sj_no: plan.sjNo ?? null,
      shipped_at: plan.shippedAt ? utc(plan.shippedAt) : null,
      is_void: false,
      void_reason: null,
      voided_at: null
    })
    const path = STATUS_PATH[plan.status]
    wxl_submission.push({
      ...audit(ulil, plan.createdAt),
      id: submissionId,
      workflow_id: WF_OFG.workflowId,
      entity: 'delivery_plan',
      entity_id: planId,
      source: 'ACTION',
      current_status_id: WF_OFG.statusId(plan.status),
      last_action_id: path.length ? WF_OFG.actionId(path[path.length - 1]) : null,
      workflow_snapshot: null
    })
    path.forEach((action, step) => {
      const [from, to] = WF_OFG.actionStatus[action]
      logNo += 1
      wxl_submission_log.push({
        ...audit(ACTION_USER[action], plan.shippedAt && step === path.length - 1 ? plan.shippedAt : plan.createdAt),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 10000 + logNo),
        workflow_id: WF_OFG.workflowId,
        submission_id: submissionId,
        action_id: WF_OFG.actionId(action),
        actor_id: WF_OFG.actorId(ACTION_ACTOR[action]),
        from_status_id: WF_OFG.statusId(from),
        to_status_id: WF_OFG.statusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason: null,
        entity: 'delivery_plan',
        entity_id: planId
      })
    })

    const released = plan.status !== 'OFG_FORECAST' && plan.status !== 'OFG_ARCHIVED'
    plan.lines.forEach((line, lineIndex) => {
      delivery_plan_line.push({
        ...audit(ulil, plan.createdAt),
        id: seedId(SEED_GROUP.deliveryPlanLine, (index + 1) * 10 + lineIndex),
        plan_id: planId,
        sku_id: fgSkuId(line.sku),
        unit_id: null,
        unit_type: line.unit,
        qty_plan: line.qty,
        qty_allocated: released ? line.qty : 0
      })
    })

    /* Order berjalan: tugas per operator, item = Karton FIFO. */
    ;(plan.tasks ?? []).forEach((spec, taskIndex) => {
      const taskId = seedId(SEED_GROUP.fgPickingTask, (index + 1) * 10 + taskIndex)
      const allStaged = spec.staged >= spec.qty
      fg_picking_task.push({
        ...audit(ulil, plan.createdAt),
        id: taskId,
        plan_id: planId,
        operator_id: USER_IDS[spec.operator],
        task_status: allStaged ? 'COMPLETED' : spec.staged > 0 ? 'IN_PROGRESS' : 'ASSIGNED',
        seq: taskIndex + 1
      })
      for (let i = 0; i < spec.qty; i++) {
        const stock = nextStock(spec.sku)
        const staged = i < spec.staged
        const label = labelRow(stock.id)
        label.label_status = staged ? 'STAGED' : 'ALLOCATED'
        if (staged) {
          label.pallet_id = null
          label.zone_id = OUTBOUND_STAGING_ZONE_ID
          OUTBOUND_PALLET_DRAWDOWN[stock.pallet] = (OUTBOUND_PALLET_DRAWDOWN[stock.pallet] ?? 0) + 1
        }
        itemNo += 1
        fg_picking_task_item.push({
          ...audit(ulil, plan.createdAt),
          id: seedId(SEED_GROUP.fgPickingItem, itemNo),
          task_id: taskId,
          label_id: stock.id,
          from_pallet_id: palletId(stock.pallet),
          container_id: null,
          discrepancy_id: null,
          substitute_of_item_id: null,
          item_status: staged ? 'STAGED' : 'PENDING'
        })
        if (staged) {
          stagingNo += 1
          fg_staging.push({
            ...audit(USER_IDS[spec.operator], plan.createdAt),
            id: seedId(SEED_GROUP.fgStaging, stagingNo),
            plan_id: planId,
            label_id: stock.id,
            qty_pack: packPer(spec.sku).karton,
            picked_by_id: USER_IDS[spec.operator],
            staging_status: 'STAGED'
          })
        }
      }
    })

    /* Order Shipped historis: label sudah SHIPPED (tanpa pallet) agar jejak pengiriman & Surat Jalan tersambung. */
    if (plan.status === 'OFG_SHIPPED') {
      plan.lines.forEach(line => {
        const oldest = HISTORIC.filter(h => h[1] === line.sku).sort((a, b) => a[2].localeCompare(b[2]))[0]
        const head = labelHead(oldest[0])
        for (let i = 0; i < line.qty; i++) {
          historicLabelNo += 1
          const level = line.unit === 'Pack' ? 'Karton' : line.unit
          const letter = level === 'Karton' ? 'C' : level === 'Bal' ? 'B' : 'S'
          const labelId = seedId(SEED_GROUP.fgLabel, 6000 + historicLabelNo)
          fg_label.push({
            ...audit(ulil, plan.createdAt),
            id: labelId,
            label_no: `JLC:${letter}:F:${head.short}:${head.seq}:${head.date}:${clock(7, 0, historicLabelNo * 3)}:B001:${String(8000 + historicLabelNo).padStart(4, '0')}`,
            sku_id: fgSkuId(line.sku),
            batch_id: batchId(oldest[0]),
            level,
            gtin: level === 'Karton' ? gtinOf(line.sku) : null,
            parent_label_id: null,
            zone_id: OUTBOUND_STAGING_ZONE_ID,
            pallet_id: null,
            container_id: null,
            label_status: 'SHIPPED',
            is_priority_lot: false,
            shipped_plan_id: planId
          })
          stagingNo += 1
          const per = packPer(line.sku)
          fg_staging.push({
            ...audit(budi, plan.shippedAt ?? plan.createdAt),
            id: seedId(SEED_GROUP.fgStaging, stagingNo),
            plan_id: planId,
            label_id: labelId,
            qty_pack: level === 'Karton' ? per.karton : level === 'Bal' ? per.bal : per.slop,
            picked_by_id: budi,
            staging_status: 'SHIPPED'
          })
        }
      })
    }
  })

  return {
    fg_label,
    delivery_plan,
    delivery_plan_line,
    fg_picking_task,
    fg_picking_task_item,
    fg_staging,
    fg_rts_task: [],
    fg_rts_task_item: [],
    fg_unpack_order: [],
    fg_unpack_source: [],
    wxl_submission,
    wxl_submission_log
  }
}

/** Kurangi `pallet.occupied` untuk Karton seed yang sudah di staging dan kosongkan pallet yang habis. */
export function applyOutboundStock(seed: SeedTables): SeedTables {
  const pallet = (seed.pallet ?? []).map(row => {
    const used = OUTBOUND_PALLET_DRAWDOWN[String(row.code)]
    if (!used) return row
    const left = Math.max(0, Number(row.occupied ?? 0) - used)
    return { ...row, occupied: left, ...(left === 0 ? { sku_id: null, unit_type: null, pallet_status: 'EMPTY' } : {}) }
  })
  return { ...seed, pallet }
}
